import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { getSelectedExhibitionId } from '@app/app.reducer';
import { Store } from '@ngrx/store';
import {
  BookingAmanoParkingReservation,
  SetProductBookingAmanoParkingReservation
} from '@products/models/booking.model';
import { GetAmanoParkingTariffReservationFee, SetAmanoParkingReservationDate } from '@products/models/parking.model';
import { environment } from '@src/environments/environment';
import { HelperService } from '@store/helpers/helper.service';
import { first, map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AmanaoParkingBookingService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly helperService = inject(HelperService);

  setBookingAmanoParkingReservations(tariffCount: number, isTariffClassificationParking: boolean) {
    return isTariffClassificationParking && tariffCount > 0
      ? Array.from(
          Array(tariffCount),
          (): BookingAmanoParkingReservation => ({
            isValid: false,
            since: null,
            until: null,
            price: 0
          })
        )
      : [];
  }

  getAmanoParkingTariffFee$(amanoParkingTariffReservationFee: GetAmanoParkingTariffReservationFee) {
    const { eventId, ticketTypeId, ticketPersonTypeId, since, until } = amanoParkingTariffReservationFee;

    let params = new HttpParams();
    params = params.append('since', since);
    params = params.append('until', until);

    return this.http
      .get(
        `${environment.protocol}${environment.webApiUrl}/event/${eventId}/ticket-groups/${ticketTypeId}/ticket-persons/${ticketPersonTypeId}/parking-fee`,
        { params, observe: 'response' }
      )
      .pipe(map((response) => response));
  }

  setAmanoParkingReservation$(
    amanoParkingReservationDate: SetAmanoParkingReservationDate
  ): Observable<GetAmanoParkingTariffReservationFee> {
    return this.store.select(getSelectedExhibitionId).pipe(
      first((eventId) => !!eventId),
      map((eventId) =>
        this.mapAmanoParkingReservationDatesToGetAmanoParkingTariffsFee(eventId, amanoParkingReservationDate)
      )
    );
  }

  mapAmanoParkingReservationDatesToGetAmanoParkingTariffsFee(
    eventId: number,
    amanoParkingReservationDate: SetAmanoParkingReservationDate
  ): GetAmanoParkingTariffReservationFee {
    const { ticketTypeId, ticketPersonTypeId, since, until } = amanoParkingReservationDate;
    return {
      eventId,
      ticketTypeId,
      ticketPersonTypeId,
      since: this.helperService.toStringWithoutOffset(since),
      until: this.helperService.toStringWithoutOffset(until)
    };
  }

  mapAmanoParkingReservationTariffPriceResponseToSetProductBookingAmanoParkingReservation(
    amanoParkingReservationDate: SetAmanoParkingReservationDate,
    amanoParkingReservationPrice: number,
    isAmanoParkingReservationValid: boolean
  ): SetProductBookingAmanoParkingReservation {
    const {
      productType,
      ticketTypeId,
      ticketPersonId,
      voucherCode,
      packageNumber,
      packageIndex,
      bookingReservationIndex
    } = amanoParkingReservationDate;

    return {
      productType,
      ticketTypeId,
      ticketPersonId,
      voucherCode,
      packageNumber,
      packageIndex,
      bookingReservationIndex,
      price: amanoParkingReservationPrice,
      isValid: isAmanoParkingReservationValid
    };
  }
}
